import { useCallback, useMemo, useState } from "react";
import { Button, Checkbox, Input } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  APPLY_CHUNK_SIZE,
  DEFAULT_END_DATE,
  contactRoleEndDateBackfillService,
  type ApplyRowResult,
  type ScanResponse,
  type ScanRow,
} from "../../../api/contactRoleEndDateBackfill.service.js";
import { downloadReviewCsv, downloadRollbackCsv } from "./backfillCsv.js";
import { ResultsTable } from "./ResultsTable.js";
import { RollbackSection } from "./RollbackSection.js";
import { countByStatus, runChunkedApply } from "./runApply.js";
import styles from "./ContactRoleEndDateBackfill.module.css";

/**
 * Backfills `end_date` on the contact roles the WP→PG migration imported as
 * unpublished but left active (SOFTSUPP-506): WordPress statuscode 2 became
 * `is_published = false` with `end_date` still NULL, so everything that reads
 * `end_date IS NULL` as "active" still counts them as live roles. The scan
 * endpoint finds the mismatch; apply writes the end date an operator chooses
 * (defaulting to the cutover date) to each approved row.
 *
 * The flow is deliberately step-by-step: scan is read-only and shows every
 * candidate first, the review CSV can — and should — be saved before anything
 * is written, and apply is a separate, explicit, confirmed step. The run is
 * chunked (APPLY_CHUNK_SIZE) because each row's pre-update value comes back
 * only in the apply response — the authoritative rollback CSV is built from
 * those responses, so a single oversized request that timed out would lose
 * the undo along with the run. A chunk that fails stops the run, and
 * everything the earlier chunks returned is kept and still downloadable.
 *
 * Everything unflagged starts selected — the missing end date is the bug, so
 * the safe default is to fix it. Rows flagged `editedAfterCutover` (a real
 * PG-side edit touched them) or `losesLastActiveRole` (ending them leaves the
 * contact with no active published role) start DESELECTED: they are the rows
 * where the blanket assumption may be wrong, so each one is an explicit
 * opt-in.
 */

type Phase = "idle" | "scanning" | "scanned" | "applying" | "done";

type ApplyProgress = {
  done: number;
  total: number;
};

/** A YYYY-MM-DD the `<input type="date">` produced — anything else is unset. */
const END_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export default function ContactRoleEndDateBackfill() {
  const { hasPermission } = usePermissions();

  const [phase, setPhase] = useState<Phase>("idle");
  const [scan, setScan] = useState<ScanResponse | null>(null);
  /** contactRoleIds — the scan's one-row-per-role key, stable across filters. */
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE);
  const [search, setSearch] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [applyProgress, setApplyProgress] = useState<ApplyProgress>({ done: 0, total: 0 });
  /** The applied run's results, kept for the rollback download. */
  const [applied, setApplied] = useState<ApplyRowResult[] | null>(null);
  /**
   * Whether the review CSV has been taken for the current scan. Only drives
   * the confirmation dialog's nudge — it never blocks applying.
   */
  const [hasReviewCsv, setHasReviewCsv] = useState(false);

  // A developer tool with a bulk blast radius, gated on developer.write like
  // its Developer-section neighbours (Office Identity Backfill, Website Relabel).
  const canUse = hasPermission("developer.write");

  const runScan = useCallback(async () => {
    setError(null);
    setApplied(null);
    setHasReviewCsv(false);
    setScan(null);
    setSelected(new Set());
    setSearch("");
    setFlaggedOnly(false);
    setPhase("scanning");
    try {
      const response = await contactRoleEndDateBackfillService.scan();
      setScan(response);
      // Unflagged rows start selected — the missing end date is the bug.
      // Flagged rows start deselected: each one is an explicit opt-in.
      setSelected(
        new Set(
          response.rows.flatMap(row =>
            row.editedAfterCutover || row.losesLastActiveRole ? [] : [row.contactRoleId],
          ),
        ),
      );
      setPhase("scanned");
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : String(scanError));
      setPhase("idle");
    }
  }, []);

  const toggleRow = useCallback((contactRoleId: string) => {
    setSelected(current => {
      const next = new Set(current);
      if (!next.delete(contactRoleId)) {
        next.add(contactRoleId);
      }
      return next;
    });
  }, []);

  const scanRows = useMemo(() => scan?.rows ?? [], [scan]);

  /**
   * Acts on the FULL scanned set, not the filtered view — the buttons name a
   * property of the rows ("unflagged"), not of whatever is on screen. A
   * flagged row the operator ticked by hand stays ticked.
   */
  const selectAllUnflagged = useCallback(() => {
    setSelected(current => {
      const next = new Set(current);
      for (const row of scanRows) {
        if (!row.editedAfterCutover && !row.losesLastActiveRole) {
          next.add(row.contactRoleId);
        }
      }
      return next;
    });
  }, [scanRows]);

  const deselectAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  /** The client-side view: text search on contact/company, flagged-only toggle. */
  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return scanRows.filter(row => {
      if (flaggedOnly && !row.editedAfterCutover && !row.losesLastActiveRole) {
        return false;
      }
      if (query === "") {
        return true;
      }
      return (
        row.contactName.toLowerCase().includes(query) ||
        row.companyName.toLowerCase().includes(query)
      );
    });
  }, [scanRows, search, flaggedOnly]);

  /** The rows this run will write: still ticked, whatever the filters show. */
  const selectedRows = useMemo(
    () => scanRows.filter(row => selected.has(row.contactRoleId)),
    [scanRows, selected],
  );
  const selectedCount = selectedRows.length;

  const endDateValid = END_DATE_PATTERN.test(endDate);

  /**
   * Hands the reviewer the full candidate set BEFORE the run — every scanned
   * row with its flags, selected or not, so a tab that dies mid-apply still
   * leaves a record of what the run was looking at.
   */
  const downloadReview = useCallback(() => {
    downloadReviewCsv(scanRows, new Date());
    setHasReviewCsv(true);
  }, [scanRows]);

  const applyBackfill = useCallback(async () => {
    setIsConfirming(false);
    setError(null);
    setPhase("applying");

    const { results, failure } = await runChunkedApply(
      selectedRows.map((row: ScanRow) => ({ contactRoleId: row.contactRoleId, value: endDate })),
      "end",
      (done, total) => setApplyProgress({ done, total }),
    );

    if (results.length === 0 && failure) {
      // Nothing came back at all, so there is no rollback data to protect and
      // no result worth showing: back to the preview with the selection intact.
      setError(failure);
      setPhase("scanned");
      return;
    }
    setApplied(results);
    setError(failure);
    setPhase("done");
  }, [selectedRows, endDate]);

  const resultCounts = useMemo(() => countByStatus(applied ?? []), [applied]);
  const appliedCount = resultCounts("applied");

  const flaggedTotal = scan
    ? scan.summary.editedAfterCutover + scan.summary.losesLastActiveRole
    : 0;

  if (!canUse) {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied} role="alert">
          <h2>Access Denied</h2>
          <p>You do not have permission to view the Contact Role End Date Backfill tool.</p>
        </div>
      </div>
    );
  }

  const isBusy = phase === "scanning" || phase === "applying";

  return (
    <div className={styles.page}>
      <PageHeader
        title="Contact Role End Date Backfill"
        subtitle="End the contact roles the WP migration imported as unpublished but left active (no end date)"
      />

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardTitle}>1 · Scan for open-ended unpublished roles</div>
        <div className={styles.toolbar}>
          <Button variant="contained" disabled={isBusy} onClick={() => void runScan()}>
            Scan
          </Button>
          <span className={styles.rowNote}>
            Read-only: finds contact roles with no end date but is_published = false. Nothing is
            written until you apply.
          </span>
        </div>
      </div>

      {phase === "scanning" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Scanning…</div>
        </div>
      ) : null}

      {phase === "applying" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Applying…</div>
          <div className={styles.progress} role="status" aria-live="polite">
            {`${applyProgress.done.toLocaleString()} / ${applyProgress.total.toLocaleString()} rows`}
          </div>
          <div className={styles.rowNote}>
            {applyProgress.total > APPLY_CHUNK_SIZE
              ? `Sent in batches of ${APPLY_CHUNK_SIZE.toLocaleString()}. Keep this tab open — the rollback CSV is built from the responses.`
              : "Keep this tab open — the rollback CSV is built from the responses."}
          </div>
        </div>
      ) : null}

      {phase === "scanned" && scan ? (
        <>
          <div className={styles.card}>
            <div className={styles.cardTitle}>2 · Review and select</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{scan.summary.total.toLocaleString()}</div>
                <div className={styles.tileLabel}>Roles without an end date</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{scan.summary.contacts.toLocaleString()}</div>
                <div className={styles.tileLabel}>Distinct contacts</div>
              </div>
              <div className={`${styles.tile} ${styles.tileWarning}`}>
                <div className={styles.tileNumber}>
                  {scan.summary.editedAfterCutover.toLocaleString()}
                </div>
                <div className={styles.tileLabel}>Edited after cutover</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>
                  {scan.summary.losesLastActiveRole.toLocaleString()}
                </div>
                <div className={styles.tileLabel}>Loses last active role</div>
              </div>
              <div className={`${styles.tile} ${styles.tileOk}`}>
                <div className={styles.tileNumber}>{selectedCount.toLocaleString()}</div>
                <div className={styles.tileLabel}>Selected — will be ended</div>
              </div>
            </div>
          </div>

          {flaggedTotal > 0 ? (
            <div className={`${styles.banner} ${styles.bannerWarning}`}>
              Flagged rows start deselected: {scan.summary.editedAfterCutover.toLocaleString()}{" "}
              edited after the cutover and {scan.summary.losesLastActiveRole.toLocaleString()} whose
              contact would lose their last active role. Tick each one to opt it in.
            </div>
          ) : null}

          <div className={styles.stack}>
            <div className={styles.toolbar}>
              <Input
                type="search"
                aria-label="Search contact or company"
                placeholder="Search contact or company…"
                className={styles.searchInput}
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
              <Checkbox
                aria-label="Flagged only"
                label="Flagged only"
                checked={flaggedOnly}
                onChange={event => setFlaggedOnly(event.target.checked)}
              />
              <span className={styles.spacer} />
              <Button variant="outlined" onClick={selectAllUnflagged}>
                Select all unflagged
              </Button>
              <Button variant="outlined" disabled={selectedCount === 0} onClick={deselectAll}>
                Deselect all
              </Button>
            </div>
            <div className={styles.toolbar}>
              <span className={styles.resultCount} aria-live="polite">
                {selectedCount.toLocaleString()} of {scanRows.length.toLocaleString()} role
                {scanRows.length === 1 ? "" : "s"} selected
                {filteredRows.length !== scanRows.length
                  ? ` · showing ${filteredRows.length.toLocaleString()} of ${scanRows.length.toLocaleString()}`
                  : ""}
              </span>
            </div>
            <ResultsTable rows={filteredRows} selected={selected} onToggle={toggleRow} />
          </div>

          <div className={styles.toolbar}>
            <Button variant="outlined" disabled={scanRows.length === 0} onClick={downloadReview}>
              Download scan report (CSV)
            </Button>
            <span className={styles.rowNote}>
              For review/Jira — not a rollback file. Every scanned row with its flags. Saves as{" "}
              <code>contact-role-end-date-review-….csv</code>.
            </span>
            <span className={styles.spacer} />
            <Input
              type="date"
              label="End date"
              value={endDate}
              onChange={event => setEndDate(event.target.value)}
            />
            <Button
              variant="contained"
              disabled={selectedCount === 0 || !endDateValid || isBusy}
              onClick={() => setIsConfirming(true)}
            >
              Apply end date ({selectedCount.toLocaleString()})
            </Button>
          </div>
        </>
      ) : null}

      {phase === "done" && applied ? (
        <>
          {resultCounts("error") > 0 ? (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              {resultCounts("error")} row{resultCounts("error") === 1 ? "" : "s"} failed to apply.
              Download the rollback CSV before retrying.
            </div>
          ) : null}

          <div className={styles.card}>
            <div className={styles.cardTitle}>Result</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileOk}`}>
                <div className={styles.tileNumber}>{appliedCount.toLocaleString()}</div>
                <div className={styles.tileLabel}>Applied</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{resultCounts("noop")}</div>
                <div className={styles.tileLabel}>No-op</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{resultCounts("skipped_already_ended")}</div>
                <div className={styles.tileLabel}>Skipped — already ended</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{resultCounts("invalid")}</div>
                <div className={styles.tileLabel}>Invalid</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{resultCounts("error")}</div>
                <div className={styles.tileLabel}>Error</div>
              </div>
            </div>
          </div>

          {/* Deliberately louder than everything else on the results panel: an
              operator once mistook the scan report for this file, and there is
              no second chance at it once the page is gone. Rendered for full
              AND partially-failed runs — the banked chunks are exactly what a
              partial failure needs rolled back. */}
          <div className={styles.callout}>
            <div className={styles.calloutTitle}>Rollback CSV — download and store this now</div>
            <div className={styles.calloutBody}>
              This file is the ONLY copy of the pre-apply state. It cannot be regenerated after you
              leave this page. It is the only file the Rollback section accepts.
            </div>
            <Button
              variant="contained"
              disabled={appliedCount === 0}
              onClick={() => {
                if (applied) {
                  downloadRollbackCsv(applied, new Date());
                }
              }}
            >
              Download rollback CSV
            </Button>
          </div>

          <div className={styles.toolbar}>
            <Button variant="outlined" onClick={() => void runScan()}>
              Scan again
            </Button>
          </div>
        </>
      ) : null}

      <RollbackSection />

      {isConfirming ? (
        <ConfirmationModal
          title="Apply contact role end dates"
          message={`${selectedCount.toLocaleString()} contact role${selectedCount === 1 ? "" : "s"} will be given end date ${endDate}.`}
          warningMessage={
            hasReviewCsv
              ? "Keep this tab open during the run — the rollback CSV offered afterwards is built from the responses and is the only undo."
              : "No scan report downloaded. Keep this tab open during the run — the rollback CSV offered afterwards is the only undo."
          }
          confirmButtonText={`End ${selectedCount.toLocaleString()} role${selectedCount === 1 ? "" : "s"}`}
          // A stray click beside the dialog must not stand in for a decision
          // about thousands of contact role rows — Cancel and Escape still do.
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={applyBackfill}
        >
          {/* A nudge, never a gate: an operator who knows what they are doing
              can still confirm without taking the review file. */}
          {hasReviewCsv ? null : (
            <Button variant="outlined" onClick={downloadReview}>
              Download scan report first
            </Button>
          )}
        </ConfirmationModal>
      ) : null}
    </div>
  );
}
